import fileSize from 'filesize';
import {useIntl} from 'react-intl';
import Hotkeys from 'react-hot-keys';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import {useResizeDetector} from 'react-resize-detector';
import React, {useMemo, useState, useEffect, useContext, useCallback} from 'react';
import {
  Box,
  Paper,
  Select,
  Toolbar,
  MenuItem,
  IconButton,
  FormControl,
} from '@material-ui/core';

import Annotator from './Annotator/Annotator';
import {SCALE_FACTOR} from './Annotator/const';
import {AnchorPointProps} from './Annotator/AnchorPoint';
import MenuList, {MenuListLocation} from './Annotator/MenuList';
import {Point, BoxLabelProps, PolyLabelProps} from './Annotator/types';
import {AnnotatorContext, NOT_SCALE_TO_FIT} from './Annotator/Provider';
import {ImageType} from '../../../types/dataset/DatasetGetAnnotationsResponse';
import {
  MAX_SCALE_FACTOR,
  MIN_SCALE_FACTOR,
  ZOOM_PAD_VERTICAL,
  ZOOM_PAD_HORIZONTAL,
} from './const';
import {useSegmentationContext} from './model/context';

import './SegmentationAnnotatorView.scss';

export type LabelDataType = {
  id: number;
  label: string;
};

export type SegmentationAnnotatorViewProps = {
  src?: string;
  image?: ImageType | null;
  labels?: LabelDataType[];
  onPredictionCompleted?: () => void;
  onDeleteFreePoint?: (point: Point) => void;
  onSelect?: (points: AnchorPointProps[]) => void;
  onFreePointCreated?: (point: AnchorPointProps['point'], isRightClick: boolean) => void;
};

const getScaleFactor = (from: number, to: number) => to / from;

export const SegmentationAnnotatorView = ({
  src,
  image,
  labels,
  onDeleteFreePoint = () => {},
  onFreePointCreated = () => {},
  onPredictionCompleted = () => {},
}: SegmentationAnnotatorViewProps) => {
  const intl = useIntl();
  const {width = 0, height = 0, ref} = useResizeDetector({
    handleWidth: true,
    handleHeight: true,
  });
  const FIT_TO_PAGE_LABEL = intl.formatMessage({
    id: 'dataPrep.detection.fitPage',
  });

  const {
    scale,
    imageLayer,
    setImageLayer,
    scaleToFitFactor,
    setScaleToFitFactor,
  } = useContext(AnnotatorContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [lastSelectedLabel, setLastSelectedLabel] = useState('');
  const [selectedAnnotationsIds, setSelectedAnnotationsIds] = useState<string[]>([]);

  const {
    activeImageId,
    activeSegmentation,

    activePolygon,
    updatePolygonLabel,

    removePoint,
    removePolygon,
    addBoundingBox,
    setActivePolygon,
    updateBoundingBox,
    removeBoundingBox,
    initializeANewPrediction,
  } = useSegmentationContext();

  const workingSegmentation = useMemo(
    () =>
      activeSegmentation?.getPolygons({includeLabeled: false, includeEmpty: false})[0],
    [activeSegmentation]
  );

  const freePoints: AnchorPointProps[] = useMemo(() => {
    let points = [];

    if (activeImageId && activeSegmentation) {
      points.push(
        ...activeSegmentation.getPositivePoints().map(point => ({
          point,
          strokeWidth: 1,
          fill: '#19B99A',
          strokeColor: '#C8EEE7',
          shadow: {
            blur: 2,
            opacity: 0.3,
            color: '#0000004D',
          },
          onClick: () => {
            removePoint(activeImageId, point);
          },
        }))
      );

      points.push(
        ...activeSegmentation.getNegativePoints().map(point => ({
          point,
          strokeWidth: 1,
          fill: '#F50157',
          strokeColor: '#FFDDDD',
          shadow: {
            blur: 2,
            opacity: 0.3,
            color: '#0000004D',
          },
          onClick: () => {
            removePoint(activeImageId, point);
          },
        }))
      );
    }

    return points;
  }, [activeImageId, activeSegmentation, removePoint]);

  const boundingBoxes: BoxLabelProps[] = useMemo(
    () => activeSegmentation?.getBoundingBoxes() ?? [],
    [activeSegmentation]
  );

  const predictions: PolyLabelProps[] = useMemo(
    () => activeSegmentation?.getPolygons({includeEmpty: false}) ?? [],
    [activeSegmentation]
  );

  const [contextLocation, setContextLocation] = useState<MenuListLocation>({
    x: 0,
    y: 0,
  });

  const onContextMenu = useCallback((event: PointerEvent) => {
    // TODO: This setTimeout is a workaround to avoid a bug with the default browser right click menu overlapping the context menu.
    // We should find a better solution. I have tried using event.preventDefault() and similar approaches.
    setTimeout(() => {
      setContextLocation({x: event.x, y: event.y});
      setMenuOpen(true);
    }, 0);
  }, []);

  const onClose = useCallback(() => {
    setMenuOpen(false);
    setContextLocation({x: 0, y: 0});
  }, []);

  const onSelectLabel = useCallback(
    async (value: string) => {
      const segmentationToEdit = workingSegmentation ?? activePolygon;

      if (activeImageId && segmentationToEdit) {
        const label = value.trim();

        setLastSelectedLabel(label);
        await updatePolygonLabel(activeImageId, segmentationToEdit.id, label);

        if (!activeSegmentation?.getPolygons({includeLabeled: false}).length) {
          await initializeANewPrediction();
        }
      }

      setMenuOpen(false);
      onPredictionCompleted();
    },
    [
      activePolygon,
      activeImageId,
      activeSegmentation,
      workingSegmentation,
      updatePolygonLabel,
      onPredictionCompleted,
      initializeANewPrediction,
    ]
  );

  useEffect(
    function resetAnnotationWhenImageChanges() {
      setSelectedAnnotationsIds([]);
    },
    [image]
  );

  useEffect(
    function changeToDefaultWhenImageSelectionChanges() {
      if (image) {
        setScaleToFitFactor(1);
      }
    },
    [image, setScaleToFitFactor]
  );

  const getScale = useCallback(() => {
    const scaleX = getScaleFactor(image?.width || 0, width - ZOOM_PAD_HORIZONTAL);
    const scaleY = getScaleFactor(image?.height || 0, height - ZOOM_PAD_VERTICAL);

    return Math.min(scaleX, scaleY);
  }, [image, width, height]);

  const onScaleSelect = useCallback(
    (newScale: number) => {
      if (newScale === NOT_SCALE_TO_FIT) {
        setScaleToFitFactor(getScale());
      } else {
        const scaleTo = Math.min(Math.max(newScale, MIN_SCALE_FACTOR), MAX_SCALE_FACTOR);

        setImageLayer({
          scale: scaleTo,
          x: imageLayer.x + (width * imageLayer.scale - width * scaleTo) / 2,
          y: imageLayer.y + (height * imageLayer.scale - height * scaleTo) / 2,
        });
        setScaleToFitFactor(NOT_SCALE_TO_FIT);
      }
    },
    [imageLayer, width, height, getScale, setScaleToFitFactor, setImageLayer]
  );

  const scaleValue = useMemo(() => {
    let scaleValue = 1;

    if (scaleToFitFactor !== NOT_SCALE_TO_FIT) {
      const scaleFactor = getScale();

      if (imageLayer.scale !== scaleFactor) {
        setImageLayer({
          scale: scaleFactor,
          x: (width - width * scaleFactor) / 2,
          y: (height - height * scaleFactor) / 2,
        });
      }

      scaleValue = NOT_SCALE_TO_FIT;
    } else {
      scaleValue = scale();
    }

    return scaleValue;
  }, [scale, scaleToFitFactor, imageLayer, width, height, setImageLayer, getScale]);

  const onBoundingBoxCreated = useCallback(
    (boundingBox: BoxLabelProps) => {
      if (activeImageId) {
        addBoundingBox(activeImageId, boundingBox);
      }
    },
    [activeImageId, addBoundingBox]
  );

  const onDeletAnnotation = useCallback(
    async (id: string) => {
      if (activeImageId) {
        const isItBoundingBoxRemoved = await removeBoundingBox(activeImageId, id);

        if (!isItBoundingBoxRemoved) {
          await removePolygon(activeImageId, id);
        }
      }
    },
    [activeImageId, removeBoundingBox, removePolygon]
  );

  const onHoverPolygon = useCallback(
    (poly: PolyLabelProps) => {
      setActivePolygon(poly);
    },
    [setActivePolygon]
  );

  const onBoundingBoxChanged = useCallback(
    (id: string, newProp: Partial<BoxLabelProps>) => {
      if (activeImageId) {
        updateBoundingBox(activeImageId, id, newProp);
      }
    },
    [activeImageId, updateBoundingBox]
  );

  const hotKey: {[key: string]: Function} = useMemo(
    () => ({
      enter: () => {
        if (activeImageId && lastSelectedLabel && workingSegmentation) {
          onSelectLabel(lastSelectedLabel);
        }
      },
    }),
    [activeImageId, workingSegmentation, lastSelectedLabel, onSelectLabel]
  );

  return (
    <Hotkeys keyName="enter" onKeyUp={(keyName: string) => hotKey[keyName]()}>
      <div className="segmentation-view__main__container" ref={ref}>
        <Paper className="segmentation-view__main" elevation={0}>
          <Toolbar className="segmentation-view__main_toolbar" variant="dense">
            <span className="segmentation-view__main_info_container">
              <span className="segmentation-view__main_filename">
                {image?.file_name || ''}
              </span>
              <span className="segmentation-view__main_info">
                {image?.height != null && image?.width != null
                  ? `${image.width}x${image.height}`
                  : ''}
              </span>
              <span className="segmentation-view__main_info">
                {fileSize(image?.size || 0) || ''}
              </span>
            </span>
            <Box flexGrow={1}></Box>
            <IconButton
              size="small"
              disabled={!image || scaleValue === MAX_SCALE_FACTOR}
              onClick={() => onScaleSelect(imageLayer.scale * SCALE_FACTOR)}
            >
              <ZoomInIcon />
            </IconButton>
            <span>
              <FormControl className="segmentation-view__main_toolbar_scale_dropdown">
                <Select
                  labelWidth={200}
                  value={scaleValue}
                  variant="standard"
                  defaultValue={scale()}
                  id="demo-simple-select"
                  labelId="demo-simple-select-label"
                  renderValue={val => {
                    return scaleToFitFactor === NOT_SCALE_TO_FIT
                      ? Math.round(Number(val) * 100) + '%'
                      : FIT_TO_PAGE_LABEL;
                  }}
                  onChange={({target}) => onScaleSelect(target.value as number)}
                >
                  <MenuItem value={NOT_SCALE_TO_FIT}>{FIT_TO_PAGE_LABEL}</MenuItem>
                  <MenuItem value={0.5}>50%</MenuItem>
                  <MenuItem value={0.75}>75%</MenuItem>
                  <MenuItem value={1}>100%</MenuItem>
                  <MenuItem value={1.25}>125%</MenuItem>
                  <MenuItem value={1.5}>150%</MenuItem>
                  <MenuItem value={2}>200%</MenuItem>
                </Select>
              </FormControl>
            </span>
            <IconButton
              size="small"
              disabled={!image || scaleValue === MIN_SCALE_FACTOR}
              onClick={() => onScaleSelect(imageLayer.scale / SCALE_FACTOR)}
            >
              <ZoomOutIcon />
            </IconButton>
          </Toolbar>
          <div>
            <Annotator
              src={src}
              image={image}
              width={width}
              height={height}
              freePoints={freePoints}
              polyLabels={predictions}
              boundingBoxes={boundingBoxes}
              selectedAnnotationsIds={selectedAnnotationsIds}
              onDelete={onDeletAnnotation}
              onContextMenu={onContextMenu}
              onHoverPolygon={onHoverPolygon}
              onSelect={setSelectedAnnotationsIds}
              onDeleteFreePoint={onDeleteFreePoint}
              onFreePointCreated={onFreePointCreated}
              onChangeBoundingBox={onBoundingBoxChanged}
              onBoundingBoxCreated={onBoundingBoxCreated}
            />
            <MenuList
              open={menuOpen}
              location={contextLocation}
              onClose={onClose}
              onSelect={onSelectLabel}
              items={labels?.map(e => e.label)}
              onDelete={() => {
                setMenuOpen(false);
                onDeletAnnotation(activePolygon?.id || '');
              }}
            />
          </div>
        </Paper>
      </div>
    </Hotkeys>
  );
};
